import { useAppSelector, useAppDispatch } from "./store/hooks";
import { useEffect } from "react";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import AuthNavbar from "./components/navigations/AuthNavbar";
import GuestNavbar from "./components/navigations/GuestNavbar";
import { setToken } from "./store/slices/authSlice";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// Guest Pages
import Home from "./pages/guest/Home";
import About from "./pages/guest/About";
import Blog from "./pages/guest/Blog";
import BlogAll from "./pages/guest/BlogAll";
import BlogRead from "./pages/guest/BlogRead";
import HelpCenter from "./pages/guest/HelpCenter";
import Support from "./pages/guest/Support";
import Terms from "./pages/guest/Terms";
import TermsOfService from "./pages/guest/TermsOfService";

// Auth Pages
import SignIn from "./pages/guest/auth/SignIn";
import SignInEmail from "./pages/guest/auth/SignInEmail";
import SignInPhone from "./pages/guest/auth/SignInPhone";
import SignUp from "./pages/guest/auth/SignUp";
import ForgotPassword from "./pages/guest/auth/ForgotPassword";
import ResetPassword from "./pages/guest/auth/ResetPassword";
import VerifyCode from "./pages/guest/auth/VerifyCode";
import VerifyAccount from "./pages/guest/auth/VerifyAccount";

// Authenticated Routes
import AuthRoutes from "./routes/authRoutes";

const ToastConfig = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  newestOnTop: false,
  closeOnClick: true,
  rtl: false,
  pauseOnFocusLoss: true,
  draggable: true,
  pauseOnHover: true,
  theme: "colored",
};

function ProtectedRoute({
  children,
  requireAuth = false,
  requireVerified = false,
  preventAuth = false,
}) {
  const { isAuth, isVrf } = useAppSelector((s) => s.auth);

  if (requireAuth && !isAuth) {
    return <Navigate to="/signin" replace />;
  }

  if (requireVerified && !isVrf) {
    return <Navigate to="/verify-account" replace />;
  }

  if (preventAuth && isAuth && isVrf) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function RouteWithNavbar({ children, showAuthNavbar = false, user = null }) {
  if (showAuthNavbar) {
    return (
      <>
        <AuthNavbar user={user} />
        {children}
      </>
    );
  }

  return (
    <>
      <GuestNavbar />
      {children}
    </>
  );
}

function App() {
  const dispatch = useAppDispatch();
  const { isAuth, isVrf, trp_usr } = useAppSelector((s) => s.auth);

  useEffect(() => {
    const token = localStorage.getItem("trp_tk");
    if (token) {
      dispatch(setToken(token));
    }
  }, [dispatch]);

  const shouldShowAuthNavbarForGuestRoutes = isAuth && isVrf;

  return (
    <>
      <ToastContainer {...ToastConfig} />
      <BrowserRouter>
        <Routes>
          {isAuth && isVrf ? (
            <Route path="/*" element={<AuthRoutes />} />
          ) : (
            <>
              {/* =================================
                  PUBLIC GUEST PAGES WITH NAVBAR
                  ================================= */}

              {/* Home Page */}
              <Route
                path="/"
                element={
                  <RouteWithNavbar
                    showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                    user={trp_usr}
                  >
                    <Home />
                  </RouteWithNavbar>
                }
              />

              {/* About Page */}
              <Route
                path="/about"
                element={
                  <RouteWithNavbar
                    showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                    user={trp_usr}
                  >
                    <About />
                  </RouteWithNavbar>
                }
              />

              {/* Help & Support Pages */}
              <Route
                path="/help"
                element={
                  <RouteWithNavbar
                    showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                    user={trp_usr}
                  >
                    <HelpCenter />
                  </RouteWithNavbar>
                }
              />

              <Route
                path="/support"
                element={
                  <RouteWithNavbar
                    showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                    user={trp_usr}
                  >
                    <Support />
                  </RouteWithNavbar>
                }
              />

              {/* Legal Pages */}
              <Route
                path="/terms"
                element={
                  <RouteWithNavbar
                    showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                    user={trp_usr}
                  >
                    <Terms />
                  </RouteWithNavbar>
                }
              />

              <Route
                path="/privacy"
                element={
                  <RouteWithNavbar
                    showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                    user={trp_usr}
                  >
                    <Terms />
                  </RouteWithNavbar>
                }
              />

              <Route
                path="/cookie-policy"
                element={
                  <RouteWithNavbar
                    showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                    user={trp_usr}
                  >
                    <Terms />
                  </RouteWithNavbar>
                }
              />

              <Route
                path="/terms-of-service"
                element={
                  <RouteWithNavbar
                    showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                    user={trp_usr}
                  >
                    <TermsOfService />
                  </RouteWithNavbar>
                }
              />

              {/* Blog Pages */}
              <Route
                path="/blog"
                element={
                  <RouteWithNavbar
                    showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                    user={trp_usr}
                  >
                    <Blog />
                  </RouteWithNavbar>
                }
              />

              <Route
                path="/blog/all"
                element={
                  <RouteWithNavbar
                    showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                    user={trp_usr}
                  >
                    <BlogAll />
                  </RouteWithNavbar>
                }
              />

              <Route
                path="/blog/:id"
                element={
                  <RouteWithNavbar
                    showAuthNavbar={shouldShowAuthNavbarForGuestRoutes}
                    user={trp_usr}
                  >
                    <BlogRead />
                  </RouteWithNavbar>
                }
              />

              {/* =================================
                  AUTHENTICATION PAGES (NO NAVBAR)
                  ================================= */}

              {/* Sign In Options */}
              <Route
                path="/signin"
                element={
                  <ProtectedRoute preventAuth={true}>
                    <SignIn />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/signin/email"
                element={
                  <ProtectedRoute preventAuth={true}>
                    <SignInEmail />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/signin/phone"
                element={
                  <ProtectedRoute preventAuth={true}>
                    <SignInPhone />
                  </ProtectedRoute>
                }
              />

              {/* Sign Up */}
              <Route
                path="/signup"
                element={
                  <ProtectedRoute preventAuth={true}>
                    <SignUp />
                  </ProtectedRoute>
                }
              />

              {/* Password Recovery */}
              <Route
                path="/forgot-password"
                element={
                  <ProtectedRoute preventAuth={true}>
                    <ForgotPassword />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/forgot-password/reset"
                element={
                  <ProtectedRoute preventAuth={true}>
                    <ResetPassword />
                  </ProtectedRoute>
                }
              />

              {/* Verification Pages */}
              <Route
                path="/verify"
                element={
                  <ProtectedRoute preventAuth={true}>
                    <VerifyCode />
                  </ProtectedRoute>
                }
              />

              <Route path="/verify-account" element={<VerifyAccount />} />

              {/* =================================
                  FALLBACK ROUTES
                  ================================= */}

              {/* Redirect unknown routes to home for guest users */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </BrowserRouter>
    </>
  );
}

export default App;
